import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DatabaseBackup, Download, RotateCcw, CloudUpload, ChevronDown, ChevronRight } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { downloadBlob } from "@/components/SignOnSetup";

const fmt = (s) => new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Australia/Sydney" });
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function EventGroup({ group, onBackup, onRestore, busy }) {
  const [open, setOpen] = useState(false);
  const latest = group.rows[0];
  const stateCls = group.state === "live" ? "text-emerald-700" : "text-red-700";
  return (
    <div className="border border-black/10 bg-white" data-testid={`backup-group-${group.event_id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <button onClick={() => setOpen(!open)} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`backup-group-toggle-${group.event_id}`}>
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className="min-w-0">
            <span className="block truncate font-display text-lg font-black tracking-tighter">{group.name}</span>
            <span className="text-xs text-zinc-500">
              <span className={`font-semibold uppercase ${stateCls}`}>{group.state}</span> · {group.rows.length} backup{group.rows.length !== 1 ? "s" : ""} · latest {fmt(latest.created_at)} · {latest.counts?.volunteers ?? 0} volunteer rows
            </span>
          </span>
        </button>
        {group.state === "live" ? (
          <button onClick={() => onBackup(group.event_id)} disabled={busy} data-testid={`backup-now-${group.event_id}`} className="inline-flex items-center gap-1.5 border border-black/20 px-3 py-1.5 text-xs hover:bg-black hover:text-white disabled:opacity-40"><CloudUpload className="h-3.5 w-3.5" /> Back up now</button>
        ) : null}
      </div>
      {open ? (
        <ul className="divide-y divide-black/5 border-t border-black/10 text-sm">
          {group.rows.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`backup-row-${b.id}`}>
              <span className="font-mono text-xs">{fmt(b.created_at)}</span>
              <span className="text-xs text-zinc-500">{b.trigger === "nightly" ? "Nightly" : `Manual${b.by ? ` by ${b.by}` : ""}`} · {kb(b.size)} · {b.counts?.volunteers ?? 0} volunteers · {b.counts?.signons ?? 0} sign-ons · {(b.counts?.mock_emails ?? 0) + (b.counts?.mock_sms ?? 0)} messages</span>
              <span className="ml-auto flex gap-2">
                <button onClick={() => downloadBlob(`/admin/backups/${b.id}/download`, `${b.event_slug || "event"}-backup-${b.created_at.slice(0, 10)}.json`)} data-testid={`backup-download-${b.id}`} className="inline-flex items-center gap-1 border border-black/20 px-2.5 py-1 text-xs hover:bg-black hover:text-white"><Download className="h-3.5 w-3.5" /> Download</button>
                <button onClick={() => onRestore(b)} disabled={busy} data-testid={`backup-restore-${b.id}`} className="inline-flex items-center gap-1 border border-[#002FA7] px-2.5 py-1 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" /> Restore as new copy</button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function AdminBackups() {
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([api.get("/admin/backups"), api.get("/admin/events")])
      .then(([b, e]) => { setRows(b.data); setEvents(e.data); })
      .catch(() => toast.error("Could not load backups"));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function backupNow(eventId) {
    setBusy(true);
    try { await api.post(`/admin/events/${eventId}/backups`); toast.success("Backup saved to cloud storage"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Backup failed"); }
    finally { setBusy(false); }
  }
  async function restore(b) {
    if (!window.confirm(`Restore "${b.event_name}" from ${fmt(b.created_at)} as a NEW draft event? The original is not changed.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`);
      toast.success(`Restored as "${data.name}" (${data.restored.volunteers} volunteer rows)`);
      navigate(`/admin/events/${data.event_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  }

  const groups = [];
  (rows || []).forEach((r) => {
    let g = groups.find((x) => x.event_id === r.event_id);
    if (!g) { g = { event_id: r.event_id, name: r.event_name, state: r.event_state, rows: [] }; groups.push(g); }
    g.rows.push(r);
  });
  const noBackup = events.filter((e) => !groups.some((g) => g.event_id === e.id));

  return (
    <AdminShell title="Backups">
      <main className="mx-auto max-w-5xl px-6 py-8" data-testid="backups-page">
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">Cloud backups</div>
        <h1 className="mt-1 flex items-center gap-2 font-display text-4xl font-black tracking-tighter"><DatabaseBackup className="h-8 w-8" /> Backups</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-600">
          Every night at 2am (Sydney) each event that changed is copied to secure cloud storage: event setup, full roster, sign-ons, emails, SMS log, bounces and export presets. Uploaded files (PDFs, images) are already stored in the cloud. Backups are kept for 30 days. Restoring always creates a <b>new draft copy</b> — the original is never touched.
        </p>
        <div className="mt-6 space-y-3">
          {rows === null ? <p className="text-sm text-zinc-500">Loading…</p> : null}
          {groups.map((g) => <EventGroup key={g.event_id} group={g} onBackup={backupNow} onRestore={restore} busy={busy} />)}
          {rows && !groups.length ? <p className="text-sm text-zinc-500" data-testid="backups-empty">No backups yet — the first nightly backup runs tonight, or use Back up now below.</p> : null}
        </div>
        {noBackup.length ? (
          <section className="mt-8">
            <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500">Not backed up yet</h2>
            <ul className="mt-2 divide-y divide-black/5 border border-black/10 bg-white text-sm">
              {noBackup.map((e) => (
                <li key={e.id} className="flex items-center justify-between px-4 py-2">
                  <span>{e.name}</span>
                  <button onClick={() => backupNow(e.id)} disabled={busy} data-testid={`backup-now-${e.id}`} className="inline-flex items-center gap-1.5 border border-black/20 px-3 py-1 text-xs hover:bg-black hover:text-white disabled:opacity-40"><CloudUpload className="h-3.5 w-3.5" /> Back up now</button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
    </AdminShell>
  );
}
